import type { Story } from "../types";

/**
 * Recent Logs belongs exclusively to the current active story.
 *
 * Do not fall back to the latest completed story: during socket/filter races
 * there can briefly be no active row in the client state, and rendering a
 * completed story in that window leaks its logs into the newly-created story.
 */
export const findActiveStoryForUser = (
  stories: Story[],
  userId?: string | null,
): Story | undefined => {
  if (!userId) return undefined;

  let newest: Story | undefined;
  let newestCreatedAt = Number.NEGATIVE_INFINITY;

  for (const story of stories) {
    if (story.status !== "active" || story.userId !== userId) continue;

    const createdAt =
      typeof story.createdAt === "number"
        ? story.createdAt
        : new Date(story.createdAt || 0).getTime();
    const comparableCreatedAt = Number.isFinite(createdAt) ? createdAt : 0;

    // `>=` keeps the previous array-order behavior as a tie-breaker while
    // making the actual creation time authoritative. Server/filter snapshots
    // are not guaranteed to arrive oldest-first.
    if (!newest || comparableCreatedAt >= newestCreatedAt) {
      newest = story;
      newestCreatedAt = comparableCreatedAt;
    }
  }

  return newest;
};

/**
 * Story completion is monotonic in the Logger.
 *
 * A delayed socket payload may still call a story active after it was closed,
 * while an authoritative server response may close a story another tab still
 * considers active. In both directions, completed must win so old logs cannot
 * become Recent Logs again.
 */
export const reconcileStoryStatus = (
  localStatus: Story["status"],
  incomingStatus: Story["status"],
): Story["status"] => {
  const completed = (status: Story["status"]) =>
    status === "complete" || (status as string) === "completed";

  if (completed(localStatus) || completed(incomingStatus)) {
    return "complete";
  }

  return incomingStatus;
};

/**
 * Keeps an optimistic active story when an older asynchronous snapshot does
 * not know about it yet.
 */
export const preserveMissingActiveStories = (
  localStories: Story[],
  snapshotStories: Story[],
  userId?: string | null,
): Story[] => {
  if (!userId) return snapshotStories;

  const snapshotIds = new Set(snapshotStories.map((story) => story.id));
  const missingActiveStories = localStories.filter(
    (story) => story.status === "active" && story.userId === userId && !snapshotIds.has(story.id),
  );

  return [...snapshotStories, ...missingActiveStories];
};
